import React from 'react'
import { User } from '@shared/types'
import { Edit, Trash2, UserPlus, MapPin } from 'lucide-react'
import { getRoleBadgeColor, formatRole, getUserInitials } from '../utils/userHelpers'
import UserPagination from './UserPagination'

interface UserTableProps {
  users: User[]
  loading: boolean
  searchTerm?: string
  roleFilter?: string
  pagination: {
    current: number
    total: number
    pages: number
    hasNext: boolean
    hasPrev: boolean
  }
  onEditUser: (user: User) => void
  onDeleteUser: (user: User) => void
  onPageChange: (page: number) => void
}

const UserTable: React.FC<UserTableProps> = ({
  users,
  loading,
  searchTerm,
  roleFilter,
  pagination,
  onEditUser,
  onDeleteUser,
  onPageChange
}) => {
  if (loading) {
    return (
      <div className="bg-card rounded-lg border border-border overflow-hidden">
        <div className="p-8 text-center">
          <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
          <p className="mt-2 text-sm text-muted-foreground">Loading users...</p>
        </div>
      </div>
    )
  }

  if (users.length === 0) {
    return (
      <div className="bg-card rounded-lg border border-border overflow-hidden">
        <div className="p-8 text-center">
          <UserPlus className="mx-auto h-12 w-12 text-muted-foreground/50" />
          <p className="mt-2 text-sm text-muted-foreground">
            {searchTerm || roleFilter ? 'No users found matching your criteria' : 'No users found'}
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-card rounded-lg border border-border overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                User
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Role
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Email
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Location
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {users.map((user) => (
              <UserTableRow
                key={user.id}
                user={user}
                onEdit={() => onEditUser(user)}
                onDelete={() => onDeleteUser(user)}
              />
            ))}
          </tbody>
        </table>
      </div>

      <UserPagination
        pagination={pagination}
        onPageChange={onPageChange}
        itemName="users"
      />
    </div>
  )
}

interface UserTableRowProps {
  user: User
  onEdit: () => void
  onDelete: () => void
}

const UserTableRow: React.FC<UserTableRowProps> = ({ user, onEdit, onDelete }) => {
  return (
    <tr className="hover:bg-muted/30">
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="flex items-center">
          <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
            <span className="text-sm font-medium text-primary">
              {getUserInitials(user.firstName, user.lastName)}
            </span>
          </div>
          <div className="ml-3">
            <div className="text-sm font-medium text-foreground">
              {user.firstName} {user.lastName}
            </div>
            <div className="text-sm text-muted-foreground">ID: {user.id}</div>
          </div>
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full border ${getRoleBadgeColor(user.role)}`}>
          {formatRole(user.role)}
        </span>
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
        {user.email}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
        <div className="flex items-center">
          <MapPin className="h-4 w-4 mr-2 text-muted-foreground" />
          <span>
            {user.branchId ? `Store ${user.branchId}` : 'Not assigned'}
          </span>
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
        <div className="flex items-center justify-end gap-2">
          <button
            onClick={onEdit}
            className="p-1 text-muted-foreground hover:text-foreground transition-colors"
            title="Edit user"
          >
            <Edit className="h-4 w-4" />
          </button>
          <button
            onClick={onDelete}
            className="p-1 text-muted-foreground hover:text-destructive transition-colors"
            title="Delete user"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </td>
    </tr>
  )
}

export default UserTable